import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
 ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { EFileService } from '../efile.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { Submission } from '../models/submission.model';
import { taxYears as stubTaxYears, submissionTypes } from './dataStub';
import { SubmissionService } from './submission.service';
import { GlobalService } from '../../global/services/global.service';
import { AppService } from '../../app.service';
import { generalConfigFromGlobal } from '../../shared/workspace-element-context';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../shared/utils/appkit-dropdown.util';
import { WorkspaceService } from '../../workspace/workspace.service';

import {
  EfileGridCallbackEvent,
  EfileGridConfig,
  EfileGridDirective,
} from '../shared/efile-grid.directive';
import { EfileGridCacheService } from '../shared/efile-grid-cache.service';
import {
  CtrlFnPlan,
  EfileCtrlFnDispatcherService,
} from '../components/scrubbing-actions/efile-ctrlfn-dispatcher.service';
import { ConfirmationModalConfig } from '../../shared/components/confirmation-modal/confirmation-modal.component';

declare const $: any;

/**
 * Submission List screen — Angular 12 port of
 * `ui/app/components/efile/efileElements/list-tpl.html`.
 *
 * Grid rendering follows `.cursor/rules/efile-grid-directive.mdc`:
 * the `[efileGrid]` directive owns the gizmoGrid lifecycle (init, data
 * load, cache, destroy). This component only:
 *   1. Builds an `EfileGridConfig` matching the legacy controller
 *      (action_id 32096 / grid_id 5746).
 *   2. Refreshes the grid when tax year / submission type filters change.
 *   3. Routes gizmoGrid callbacks into the existing `SubmissionService`
 *      drill-down (submission detail tab).
 *
 * There is NO synthetic `activeScreen` hydration, no `AppService.ROUTES`
 * mutation, and no `[gg-grid]` directive usage here — those are explicit
 * anti-patterns in the efile-grid-directive cursor rule.
 */
const SUBMISSIONS_ACTION_ID = 32096;
const SUBMISSIONS_GRID_ID = '5746';
const SUBMISSIONS_CACHE_KEY = 'efile/submissions-dashboard';

@Component({
    selector: 'gtw-efile-submission',
    templateUrl: './submission-data.component.html',
    styleUrls: ['./submission-data.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SubmissionDataComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(EfileGridDirective) efileGrid?: EfileGridDirective;

  selectedSubmissions: Submission[];
  baseURLs: GtwElementsBaseUrlsModel = {
    api: '/gtw',
    custom: '/custom',
    admin: '/admin',
    efile: '/gtw-efile-api',
    pdfEngine: '/pdf-engine',
    bulkPdfEngine: '/bulk-pdf-engine',
  };
  /**
   * `taxYear` is typed `any` because `p-dropdown`'s `[(ngModel)]` binds
   * directly to the option `value` (number). Downstream code (extra-params
   * / submission-detail) accepts both string and number.
   *
   * Both `taxYear` and `taxYears` are hydrated from
   * `GlobalService.inputs.tax_year` in `applyWorkspaceContext()` — the same
   * source the legacy `SubmissionListCtrl` reads (see
   * `EfileElementsController.js` L116:
   *   `vm.taxYears = GlobalService.inputs.tax_year.values;`)
   * The stub fallback keeps the UI functional if GlobalService hasn't yet
   * emitted a tax-year payload (initial boot, detached CE host, etc.).
   */
  taxYear: any = null;
  taxYears: Array<{ value: any; label: string; [k: string]: any }> = [];
  taxYearItems: Array<{ value: any; label: string }> = [];
  selectedTaxYearItem: any = null;
  submissionTypes = submissionTypes;
  submissionTypeItems: Array<{ value: any; label: string }> = [];
  selectedSubmissionTypeItem: any = null;
  submissionType: string = 'federal';
  showTypeFilter: boolean = true;
  selectedTab: number = 0;

  /** Config consumed by `[efileGrid]`. Rebuilt as a new object on filter change. */
  gridConfig!: EfileGridConfig;

  /** Tracks the directive's data-fetch state so we can spin the refresh icon. */
  loading = false;

  /**
   * Flips to true after the first `gridDataLoaded` callback. Used together
   * with `loading` to mask the empty gizmoGrid scaffold (toolbar + scrollbar
   * stubs) the directive paints synchronously on mount, so the user sees a
   * single centered spinner in the grid section instead of an empty toolbar
   * during the initial fetch.
   */
  gridFirstLoaded = false;

  /**
   * The grid is hidden until the user explicitly clicks Filter (or a deep-link
   * forces a submission lookup). This matches the legacy efile workflow —
   * the user picks tax_year + type, then clicks Filter to populate the grid.
   */
  filterApplied = false;

  @Input('submission-type')
  set submissionTypeInput(value: string | undefined | null) {
    if (typeof value === 'string' && value.trim().length > 0) {
      this.submissionType = value.trim().toLowerCase();
    }
  }

  @Input('show-type-filter')
  set showTypeFilterInput(value: boolean | string | undefined | null) {
    if (value === undefined || value === null || value === '') {
      this.showTypeFilter = true;
      return;
    }
    if (typeof value === 'boolean') {
      this.showTypeFilter = value;
      return;
    }
    const normalized = String(value).trim().toLowerCase();
    this.showTypeFilter = !(normalized === 'false' || normalized === '0' || normalized === 'no');
  }

  private readonly subs = new Subscription();

  clientKey: number = 0;

  /**
   * Confirm-action modal state — mirrors the AngularJS
   * `app/templates/modals/confirm-action.html` modal that the legacy
   * submissions-list `ctrlFn` (`EfileElementsController.js` L244-263) opens
   * before firing validate / validateBatch / generateBatch / etc. When the
   * dispatcher's plan has a `confirmConfig`, we render `<gtw-confirmation-modal>`
   * with that config and only call `plan.execute()` after the user confirms.
   */
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingPlan: CtrlFnPlan | null = null;

  /**
   * Deep-link payload parsed from the current URL:
   *   /submissions-dasboard/submission/:id(/:subState)
   * Filled in ngOnInit from ActivatedRoute.params and consumed after the
   * submissions grid finishes loading (see `applyPendingDeepLink`).
   */
  private pendingDeepLink: { submissionId: number; subState?: string } | null = null;
  // Public so the template can gate `list-pane--hidden` / the
  // "Loading submission…" overlay on whether the deep-link round-trip
  // has finished (either by finding the row or warning past it).
  deepLinkApplied = false;
  /**
   * Last submission id that was either deep-link-resolved or activated via
   * `switchToTab`. Drives the "is this a new submission?" classifier in
   * `capturePendingDeepLink` independently of `pendingDeepLink` (which is
   * cleared as soon as the initial deep-link finishes), so a subsequent
   * sub-state-only navigation (e.g. clicking a different return-review
   * leaf) does NOT get reclassified as a fresh deep-link and incorrectly
   * reset `deepLinkApplied`/`filterApplied`.
   */
  private lastAppliedSubmissionId: number | null = null;
  private loadingSubmissionId: number | null = null;
  private pendingTabActivationIndex: number | null = null;
  /**
   * Per-submission URL-segment memory, keyed by `SUBMISSION_ID` (string).
   *
   * STATIC so it survives the cross-config component recreation that
   * Angular Router triggers whenever a navigation crosses between
   *   - `/submission/:id/:subState`           (3-segment route)
   *   - `/submission/:id/:parentState/:subState` (4-segment route)
   *
   * Without persistence, the NEW component instance starts with an empty
   * map, so a follow-up `selectedTabChange()` that targets a *different*
   * submission tab can't recover that tab's last sub-state — it would
   * fall back to the active tab's segments and either send the user to
   * the wrong screen or to the dashboard. Keeping it static makes the
   * memory truly per-tab, mirroring the legacy ui-router child-state
   * stickiness that AngularJS got "for free" via $stateParams cache.
   */
  private static submissionRouteSegments: Record<string, string[]> = {};

  /**
   * `true` whenever the current URL carries a submission `:id` segment
   * (i.e. `/submissions-dasboard/submission/:id/...`), `false` for the bare
   * `/submissions-dasboard/list` route. Used by the template to suppress
   * the list-pane render while a submission deep-link is mid-flight — we
   * still need the list grid to fire its request (so `applyPendingDeepLink`
   * can resolve the row) but the pane should NOT visually paint over the
   * submission tab the user actually requested.
   *
   * Without this gate, the cached-tab fast path occasionally races the
   * cross-config recreation (e.g. clicking a `return-review` sub-tab while
   * `submissionTabs` has stale state) and the user sees the Submission List
   * grid flash for the duration of the deep-link round-trip — exactly the
   * "URL is fine but the page redirects to submission list" symptom.
   */
  isSubmissionUrl = false;

  /**
   * Currently rendered sub-state inside the active submission tab — driven
   * by the `:subState` URL segment. Mirrors the legacy ui-router child
   * state (`submissions-dasboard.submission.<sub>`). `<submission-detail>`
   * consumes this via `[subState]` and renders the correct screen inline
   * instead of using a nested `<router-outlet>` (which can't resolve
   * before the mat-tab body mounts).
   *
   * Default is an empty string (NOT `'dashboard'`) so that during the brief
   * window after a cross-config route swap recreates this component but
   * before `capturePendingDeepLink` (called synchronously from `ngOnInit`)
   * resolves the URL, `<submission-detail>`'s `[ngSwitch]="subState"` falls
   * to its `*ngSwitchDefault` loader branch instead of mounting
   * `<gtw-efile-elements-dashboard>` and firing its chart HTTP for a URL
   * like `/return-review/<leaf>`. See the inline comment on the
   * `*ngSwitchDefault` element in `submission-detail.component.html` for
   * the full rationale (the "URL is /return-review but dashboard charts
   * loaded" symptom).
   */
  activeSubState: string = '';

  /**
   * For nested legacy URLs that fall under a parent state's internal
   * navigation (efile-scrubbing.list/run/history, return-review.<step>,
   * etc.), this carries the child segment so the parent component can
   * pre-select its internal tab. Empty for flat / Pattern-A URLs.
   *
   * Examples:
   *   `/submission/:id/efile-scrubbing/list`        → activeChildSubState='list'
   *   `/submission/:id/return-review/efile-diagnostics` → activeChildSubState='efile-diagnostics'
   *   `/submission/:id/data/add-edit`               → activeChildSubState='' (Pattern A)
   */
  activeChildSubState: string = '';

  /**
   * Full URL-segment trail for the active sub-state. Pattern A URLs need
   * BOTH segments preserved when the tab index flips (e.g. on deep-link
   * activation of `/submission/:id/data/add-edit`, a subsequent
   * `selectedTabChange()` user click used to navigate with only
   * `[id, this.activeSubState]` — collapsing the legacy two-segment path
   * to `/submission/:id/add-edit` and dropping the `/data/` parent.
   * Tracking the full segment list here keeps the URL faithful to
   * `submissions-dasboard.submission.data.addEdit` (efile.js L149).
   *
   * Defaults to `['dashboard']` — the legacy landing child state.
   */
  private activeUrlSegments: string[] = ['dashboard'];

  /** Canonical legacy sub-state keys + their synonyms → normalized key. */
  private static readonly SUB_STATE_ALIAS_MAP: Record<string, string> = {
    'dashboard': 'dashboard',
    'submit': 'submit',
    'submit.history': 'submit',
    'return-review': 'return-review',
    'efile-scrubbing': 'efile-scrubbing',
    'efilescrubbing': 'efile-scrubbing',
    'setup': 'setup',
    // Each of these legacy ui-router children (efile.js L186, L200, L207) is
    // surfaced as its own sidebar entry now. The route segment is the sub-state
    // key and `submission-detail.html` renders `<gtw-efile-setup>` with the
    // matching `[initialTab]` so the corresponding tab is pre-selected.
    'enabled-disabled': 'enabled-disabled',
    'enableddisabled': 'enabled-disabled',
    'unlock-validation': 'unlock-validation',
    'unlockvalidation': 'unlock-validation',
    'formelements-report': 'formelements-report',
    'formelementsreport': 'formelements-report',
    'overrides': 'overrides',
    'pdf-upload': 'pdf-upload',
    'pdfupload': 'pdf-upload',
    // Validation step (legacy `submissions-dasboard.submission.errors` —
    // url `/errors`, see ui/app/components/efile/efile.js L142-148). The
    // sidebar now labels this entry "Validation" so we accept both URL
    // synonyms and normalize them to the canonical `validation` sub-state.
    'errors': 'validation',
    'validation': 'validation',
    'key-xml-gen': 'key-xml-gen',
    'keyxmlgen': 'key-xml-gen',
    'xml-compare': 'xml-compare',
    'xmlcompare': 'xml-compare',
    'efile-data': 'efile-data',
    'data': 'efile-data',
    // Phase 3 context-action screens (efile.js L149, L156, L172, L179, L193, L214)
    'xml-upload': 'xml-upload',
    'xmluploadkeyid': 'xml-upload',
    'delete-form': 'delete-form',
    'deleteform': 'delete-form',
    'lock-unlock': 'lock-unlock',
    'lockunlock': 'lock-unlock',
    'xml-sync': 'xml-sync',
    'xmlsync': 'xml-sync',
    'amendsuperseded-returns': 'amendsuperseded-returns',
    'amendsupersededreturns': 'amendsuperseded-returns',
    'add-edit': 'add-edit',
    'addedit': 'add-edit',
    'data.addedit': 'add-edit',
  };

  /**
   * Parent sub-states whose second URL segment is an *internal* tab key
   * (Pattern B), not a top-level sidebar screen. Must be checked before
   * `isKnownSubState(child)` — otherwise `return-review/dashboard` is
   * misread as the submission Dashboard because `dashboard` is a known key.
   */
  private static readonly PATTERN_B_PARENT_STATES = new Set([
    'return-review',
    'efile-scrubbing',
  ]);

  constructor(
    private efileService: EFileService,
    private submissionService: SubmissionService,
    private gtwGlobal: GlobalService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private ctrlFnDispatcher: EfileCtrlFnDispatcherService,
    private gridCache: EfileGridCacheService,
    private zone: NgZone,
    private workspaceService: WorkspaceService,
  ) {
    this.selectedSubmissions = submissionService.submissionTabs;
    // -------------------------------------------------------------
    // Defensive: only seed `selectedTab` from the singleton's
    // `activeSubmissionTabIndex` when there is actually a submission
    // tab at that index. If `submissionTabs` is empty (e.g. fresh
    // page load, or every tab was just closed), force `selectedTab`
    // to 0 so the list pane mounts on first paint — even if some
    // earlier code path forgot to reset
    // `activeSubmissionTabIndex`. This is the safety-net counterpart
    // to the explicit reset inside `closeSubmissionTab`.
    // -------------------------------------------------------------
    const cachedIndex = submissionService.activeSubmissionTabIndex;
    if (
      cachedIndex > 0 &&
      cachedIndex <= this.selectedSubmissions.length
    ) {
      this.selectedTab = cachedIndex;
    } else {
      this.selectedTab = 0;
      // Keep the singleton in sync so subsequent reads (e.g. another
      // recreated instance landing here before we finish ngOnInit)
      // see the same index.
      if (cachedIndex !== 0) {
        submissionService.activateSubmissionByIndex(0);
      }
    }
  }

  ngOnInit(): void {
    // 🔴 CRITICAL: hydrate workspaceFactory.activeScreen + globalParams
    // BEFORE buildGridConfig() runs, otherwise the first grid request
    // (and any service launched from a row callback) goes out with the
    // PREVIOUS workspace screen's activity_key / screen_key / jcd_ta_key.
    // See `ensureWorkspaceContext()` for the full rationale.
    this.ensureWorkspaceContext();
    this.applyWorkspaceContext();
    this.buildGridConfig();
    // Legacy parity — `efile.js` `getCustomDataCache` (L713-733) checked the
    // workspace cache on entry and, when it found a previous response, set
    // `vm.filterMatch = true` so the grid re-rendered immediately instead of
    // pinning the user to the "Select tax year… click Filter" hint. Angular
    // Router destroys + recreates this component on every list ↔ submission
    // hop (different route configs share the same component class), so
    // without this restore the user clicks Filter again on EVERY return to
    // /list — and the data appears to "not load" despite the response
    // being one cache lookup away.
    //
    // The directive's own cache lookup (`fetchAndLoad` → `cacheService.get`)
    // takes care of paint-from-cache once the grid is mounted; we only need
    // to flip the gating flag so the *ngIf actually mounts it.
    if (this.gridCache.has(SUBMISSIONS_CACHE_KEY)) {
      this.filterApplied = true;
      // Skip the centred "Loading submissions…" spinner overlay — cached data
      // paints synchronously during gizmoGrid init, so the overlay would
      // briefly cover an already-rendered grid for no reason.
      this.gridFirstLoaded = true;
    }
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );

    // ------------------------------------------------------------------
    // Synchronous deep-link resolution BEFORE Angular's first CD pass.
    //
    // `route.params` is a Subject-backed Observable in Angular Router. Even
    // though it generally replays the current value on subscription, the
    // emission ordering relative to the new component's first paint is
    // not guaranteed once a cross-config route swap recreates this
    // component (e.g. clicking Edit on the Submission List grid →
    // `/list` is destroyed → `/submission/:id/dashboard` is created).
    //
    // At debug-time, breakpoints and inspector pauses give the params
    // microtask plenty of headroom to fire before paint, so the
    // cached-tab fast path inside `capturePendingDeepLink` flips
    // `selectedTab` to the right index in time and the new submission
    // tab renders as active. At runtime that headroom collapses — the
    // new instance can paint with `selectedTab = 0` (the class default)
    // before the params handler runs, which paints the Submission List
    // grid where the freshly-opened tab is supposed to be. That's the
    // "switchToTab works in debug but not at runtime" symptom.
    //
    // `ActivatedRoute.snapshot.params` is populated synchronously by the
    // time `ngOnInit` runs, so reading it here lets the fast path
    // mutate `selectedTab`/`isSubmissionUrl` ahead of the first CD pass.
    // We still subscribe below to handle subsequent param changes
    // without recreation (e.g. switching sub-state on the same component
    // instance via the sidebar). The fast path is idempotent so the
    // duplicate first call is harmless.
    // ------------------------------------------------------------------
    this.capturePendingDeepLink(this.route.snapshot.params as { [k: string]: string });
    this.subs.add(
      this.route.params.subscribe((params) => {
        this.capturePendingDeepLink(params);
      })
    );

    // ------------------------------------------------------------------
    // CE-INSTANCE URL TRACKING.
    //
    // The CE-mounted instance is wired to the ROOT `ActivatedRoute`,
    // which never re-emits `route.params` because its parameter map is
    // permanently empty. As a result the subscription above is silent
    // for CE instances and they only ever see the URL once (during the
    // initial `ngOnInit` snapshot read). To keep the CE in sync with
    // sidebar navigations / sub-state hops, listen to `Router.events`
    // for `NavigationEnd` and re-run the deep-link resolver against the
    // freshly-routed URL. The router-mounted instance gets the same
    // updates via `route.params`, so we only attach this for CE.
    // ------------------------------------------------------------------
    const routeConfigPresent = this.route?.snapshot?.routeConfig != null;
    if (!routeConfigPresent) {
      this.subs.add(
        this.router.events.pipe(
          filter((e) => e instanceof NavigationEnd)
        ).subscribe(() => {
          // Pass `{}` — `capturePendingDeepLink` will detect the CE
          // instance and reconstruct params from `router.url`.
          this.capturePendingDeepLink({});
        })
      );
    }
  }


  ngAfterViewInit(): void {
    // Grid auto-loads via the directive (autoLoad defaults to true). Nothing
    // else is needed here — the directive runs its own initGrid() in
    // ngAfterViewInit and kicks off the first loadGridJson request.
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Context / config
  // ---------------------------------------------------------------------------

  /**
   * Hydrate `workspaceFactory.activeScreen` + `GlobalService.globalParams`
   * with the Tax Payer / Submissions Dashboard screen entry from
   * `USER_MENU`.
   *
   * **Why this is required.** `SubmissionDataComponent` is mounted by
   * `EfileRoutingModule` directly on the top-level routes
   * `/submissions-dasboard/list` and `/submissions-dasboard/submission/:id`
   * — it does NOT go through `WorkspaceScreenComponent`. The latter is the
   * only place that calls `WorkspaceService.setActivity(...)` →
   * `loadTab(...)` → `globalService.setFilters(activeScreen)` to push the
   * active screen's `activity_key` / `screen_key` / `jcd_ta_key` /
   * `screen_type` / `is_locked` into `globalParams`.
   *
   * Without this hook, three real entry paths leave `globalParams` with
   * STALE keys from the previously-visited workspace screen (or empty on
   * first boot):
   *   1. Hard refresh on `/submissions-dasboard/list?client_key=92`.
   *   2. Deep-link to a submission (`/submission/:id/dashboard`).
   *   3. Sibling-config navigation (`/list` ↔ `/submission/:id/...`)
   *      where Angular recreates this component but no menu click fires.
   *
   * Downstream services (the grid directive's first `loadGridJson`,
   * any control-fn modal launched from a grid callback, the validate /
   * generate-batch dispatcher, etc.) read these keys directly from
   * `globalParams`, so a stale value silently goes out on the wire and
   * the backend either returns the wrong screen's data or rejects the
   * request.
   *
   * The fix routes through the official `WorkspaceService.setActivity`
   * API (same path `AppComponent.navigateWorkspaceScreen` uses), which:
   *   - resolves the real menu entry via `findScreenByLink`
   *   - sets `workspaceFactory.activeScreen` to that real entry
   *     (NOT a synthetic shape — the cursor rule's "no synthetic
   *     activeScreen hydration" applies to fabricated objects, not to
   *     real `USER_MENU` lookups)
   *   - calls `setFilters(workspace)` which pushes the screen's keys
   *     into `globalParams`
   *
   * The matching `submissions-dasboard.list` entry in
   * `WorkspaceService.TOP_LEVEL_LEGACY_STATE_COMMANDS` makes the
   * `setTabActive` emission a navigation no-op (router URL already
   * matches `/submissions-dasboard/list`) so we don't bounce the user.
   */
  private ensureWorkspaceContext(): void {
    const candidates = [
      'workspace.submissions-dasboard.list',
      'submissions-dasboard.list',
    ];

    let found: { activity: any; screen: any } | null = null;
    for (const link of candidates) {
      found = this.workspaceService.findScreenByLink(link);
      if (found?.activity && found?.screen) break;
    }
    if (!found?.activity || !found?.screen) {
      // USER_MENU doesn't expose the screen yet (initial boot before
      // `loadUserMenu` resolves) — `globalParamsChanged$` will retrigger
      // `applyWorkspaceContext()` later; the next user click on the side
      // nav will route through `AppComponent.navigateWorkspaceScreen`,
      // which DOES call `setActivity` and will push the right keys.
      return;
    }

    const activeKey = this.workspaceService.workspaceFactory?.activeScreen?.screen_key;
    if (
      activeKey != null &&
      String(activeKey) === String(found.screen.screen_key)
    ) {
      // Already active — typically the side-nav click path (Path A in
      // the comment block above). `setFilters` already ran via
      // `loadTab`; re-running setActivity would just re-emit
      // `setTabActive` for no benefit.
      return;
    }

    this.workspaceService.setActivity(found.activity, true, found.screen);
  }

  private applyWorkspaceContext(): void {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.efileService.setBaseUrls(this.baseURLs);
    this.clientKey = Number(this.gtwApp.USER_SETTINGS?.user?.client_key) || 0;
    this.hydrateTaxYears();
  }

  /**
   * Port of the legacy `SubmissionListCtrl` hydration
   * (`EfileElementsController.js` L116):
   *   `vm.taxYears = GlobalService.inputs.tax_year.values;`
   *
   * We read the workspace-scoped tax-year list from GlobalService so the
   * dropdown always matches the list the workspace header exposes. The
   * initial `taxYear` falls back through:
   *   1. `global.inputs.tax_year.selected.value`  (header selection)
   *   2. `globalParams.tax_year`                  (filter-params cache)
   *   3. the first entry in the hydrated list
   *   4. the hardcoded stub                       (boot / detached host)
   *
   * `globalParamsChanged$` re-invokes `applyWorkspaceContext()`, so the
   * dropdown will re-sync if the user picks a new tax year at the
   * workspace level. We only re-seed `this.taxYear` when the current
   * value is no longer valid within the refreshed list — that preserves
   * a user's in-screen selection across unrelated global changes.
   */
  private hydrateTaxYears(): void {
    const input = this.gtwGlobal.global?.inputs?.tax_year as any;
    const values: any[] = Array.isArray(input?.values) ? input.values : [];

    if (values.length > 0) {
      this.taxYears = values;
    } else if (this.taxYears.length === 0) {
      // First-boot fallback: surface the stub so the user can at least
      // pick something rather than seeing an empty dropdown. Replaced on
      // the next `globalParamsChanged$` emission.
      this.taxYears = stubTaxYears;
    }

    const selectedFromGlobal = input?.selected?.value;
    const selectedFromParams = this.gtwGlobal.global?.globalParams?.tax_year;
    const fallback =
      selectedFromGlobal ??
      selectedFromParams ??
      this.taxYears[0]?.value ??
      null;

    const currentIsValid =
      this.taxYear != null &&
      this.taxYears.some((ty) => String(ty?.value) === String(this.taxYear));
    if (!currentIsValid && fallback != null) {
      this.taxYear = fallback;
    }
    this.refreshTaxYearDropdownItems();
    this.refreshSubmissionTypeDropdownItems();
  }

  private refreshTaxYearDropdownItems(): void {
    this.taxYearItems = mapToAppkitDropdownItems(this.taxYears, 'label', 'value');
    this.selectedTaxYearItem = findAppkitItemByValue(this.taxYearItems, this.taxYear);
  }

  private refreshSubmissionTypeDropdownItems(): void {
    this.submissionTypeItems = mapToAppkitDropdownItems(this.submissionTypes, 'label', 'value');
    this.selectedSubmissionTypeItem = findAppkitItemByValue(this.submissionTypeItems, this.submissionType);
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  onSubmissionTypeSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedSubmissionTypeItem = selected;
    }
  }

  private buildGridConfig(): void {
    this.gridConfig = {
      actionId: SUBMISSIONS_ACTION_ID,
      gridId: SUBMISSIONS_GRID_ID,
      extraParams: this.buildExtraParams(),
      autoLoad: true,
      cacheKey: SUBMISSIONS_CACHE_KEY,
    };
  }

  private buildExtraParams(): Record<string, any> {
    return {
      tax_year: this.taxYear,
      type: this.submissionType,
    };
  }

  // ---------------------------------------------------------------------------
  // Filter / refresh actions — drive the directive through its public API
  // ---------------------------------------------------------------------------

  refresh(): void {
    // Don't attempt to refresh when the grid has not been mounted yet —
    // clicking the refresh icon before Filter is a no-op.
    if (!this.filterApplied) return;
    this.gridFirstLoaded = false;
    this.efileGrid?.clearCache();
    this.efileGrid?.refresh(this.buildExtraParams());
  }

  filterClick(): void {
    const extraParams = this.buildExtraParams();
    this.gridConfig = {
      ...this.gridConfig,
      extraParams,
    };

    // First Filter click — mount the grid. The directive's ngAfterViewInit
    // will call initGrid() and autoLoad will fetch the first page.
    if (!this.filterApplied) {
      this.filterApplied = true;
      this.gridFirstLoaded = false;
      this.cdr.detectChanges();
      return;
    }

    this.gridFirstLoaded = false;
    this.efileGrid?.clearCache();
    this.efileGrid?.refresh(extraParams);
  }

  onGridDataLoaded(data: any): void {
    this.gridFirstLoaded = true;
    this.applyPendingDeepLink(data);
    this.cdr.detectChanges();
    // gizmoGrid binds `$(window).resize(gg.resize)` — after Angular removes
    // `.gg-loading` and the flex host gets its final height, force a layout
    // pass so `.gg-body` vertical scroll matches the admin grid behavior.
    const bump = () => {
      try {
        if (typeof $ !== 'undefined' && $(window)?.trigger) {
          $(window).trigger('resize');
        }
      } catch {
        /* ignore */
      }
    };
    setTimeout(bump, 0);
    setTimeout(bump, 80);
    setTimeout(bump, 250);
  }

  // ---------------------------------------------------------------------------
  // Deep-link reconstruction
  // ---------------------------------------------------------------------------

  /**
   * Parse the ActivatedRoute params into a pending deep-link request.
   * Legacy routes:
   *   /submissions-dasboard/submission/:id
   *   /submissions-dasboard/submission/:id/:subState
   *
   * The submission payload is not in hand yet (the grid hasn't responded),
   * so we only remember the intent; `applyPendingDeepLink()` finishes the
   * job once the grid data arrives.
   */
  private capturePendingDeepLink(params: { [k: string]: string }): void {
    let routeConfigPresent = true;
    try {
      routeConfigPresent = this.route?.snapshot?.routeConfig != null;
    } catch {
      routeConfigPresent = true;
    }
    const isCustomElementInstance = !routeConfigPresent;
    if (isCustomElementInstance) {
      params = this.parseSubmissionParamsFromUrl(this.router?.url || '');
    }

    const rawId = params?.['id'];
    // The URL is authoritative for the active sub-state. `<submission-detail>`
    // reads `activeSubState` via @Input and renders the matching screen.
    //
    // Nested URLs come in two flavors (mirroring legacy ui-router semantics):
    //   Pattern A — both segments are sidebar-addressable child states:
    //               `/submission/:id/data/add-edit`
    //               (legacy `submissions-dasboard.submission.data.addEdit`,
    //                efile.js L149) → render `gtw-efile-add-edit`, breadcrumb
    //                shows "Data > Add / Edit".
    //
    //   Pattern B — parent is a sidebar entry whose component owns its own
    //               internal tabs/steps; child is one of those internal keys:
    //               `/submission/:id/efile-scrubbing/list`
    //               `/submission/:id/return-review/efile-diagnostics`
    //               (legacy `efileScrubbing.list`, `return-review.<leaf>`).
    //               Render the PARENT component and stash the child key in
    //               `activeChildSubState` so it can pre-select its tab.
    //
    // Disambiguation: if the child segment is in our SUB_STATE_ALIAS_MAP,
    // it's Pattern A; otherwise fall back to the parent (Pattern B).
    const rawSubState = params?.['subState'];
    const rawParentState = params?.['parentState'];
    const childKnown = this.isKnownSubState(rawSubState);
    const parentKnown = this.isKnownSubState(rawParentState);

    // Parents that OWN their child segment via internal tab/step routing
    // (legacy ui-router nesting). The child belongs to the parent, even
    // when it happens to alias a top-level sub-state.
    //
    // Concrete case this guards: clicking the "IRS Validations" workflow
    // tab inside Return Review navigates to
    //   /submission/:id/return-review/errors
    // The child `errors` is in SUB_STATE_ALIAS_MAP (it doubles as the
    // legacy *standalone* `submissions-dasboard.submission.errors` URL,
    // efile.js L142-148). Without this guard the disambiguation below
    // would short-circuit into "Pattern A nested" and render the
    // standalone `<gtw-efile-validation-errors>` host — dropping the
    // Return Review workflow tab strip (eFile Validations / IRS
    // Validations / GTW Diagnostics / …). Forcing Pattern B here keeps
    // `<gtw-efile-return-review>` mounted with `childSubState='errors'`
    // so it pre-selects the validation step AND shows the tab strip.
    const parentOwnsChildRouting = !!rawParentState && (
      rawParentState === 'return-review' ||
      rawParentState === 'efile-scrubbing'
    );

    let normalizedSubState: string;
    let childForward: string;
    let urlSegments: string[];
    if (parentOwnsChildRouting && parentKnown) {
      // Pattern B (forced) — parent owns the child routing. Render the
      // parent and forward the child for its internal tab/step routing
      // (e.g. `return-review/dashboard`, `return-review/errors`).
      normalizedSubState = this.normalizeSubState(rawParentState);
      childForward = (rawSubState || '').toLowerCase();
      urlSegments = [
        rawParentState.trim().toLowerCase(),
        (rawSubState || '').trim().toLowerCase(),
      ].filter(Boolean);
    } else if (childKnown && rawParentState) {
      // Pattern A nested — both segments are addressable. Preserve BOTH
      // in the URL trail so a later `selectedTabChange()` push doesn't
      // collapse the legacy `/data/add-edit` path back to `/add-edit`.
      normalizedSubState = this.normalizeSubState(rawSubState);
      childForward = '';
      urlSegments = [
        rawParentState.trim().toLowerCase(),
        (rawSubState || '').trim().toLowerCase(),
      ].filter(Boolean);
    } else if (childKnown || !rawParentState) {
      // Pattern A flat or single-segment (`/submission/:id/<sub>`).
      normalizedSubState = this.normalizeSubState(rawSubState);
      childForward = '';
      urlSegments = [(rawSubState || 'dashboard').trim().toLowerCase()];
    } else if (parentKnown) {
      // Pattern B — fall back to the parent so the right component renders;
      // forward the unknown child for the parent's internal tab routing.
      // The URL trail keeps both segments so back/forward retains the leaf.
      normalizedSubState = this.normalizeSubState(rawParentState);
      childForward = (rawSubState || '').toLowerCase();
      urlSegments = [
        rawParentState.trim().toLowerCase(),
        (rawSubState || '').trim().toLowerCase(),
      ].filter(Boolean);
    } else {
      // Neither segment recognized — safest default is the dashboard.
      normalizedSubState = 'dashboard';
      childForward = '';
      urlSegments = ['dashboard'];
    }

    this.activeSubState = normalizedSubState;
    this.activeChildSubState = childForward;
    this.activeUrlSegments = urlSegments.length > 0 ? urlSegments : ['dashboard'];
    if (rawId != null && rawId !== '') {
      SubmissionDataComponent.submissionRouteSegments[String(rawId)] = [...this.activeUrlSegments];
    }

    if (rawId == null || rawId === '') {
      // URL is /submissions-dasboard/list (no :id segment). Do NOT reset
      // selectedTab here — `closeSubmissionTab` and `selectedTabChange(0)`
      // already set it to 0 when the user explicitly returns to the list.
      this.isSubmissionUrl = false;
      this.pendingDeepLink = null;
      return;
    }
    // From here on the URL is `/submission/:id/...` — flag it so the
    // template gates the list-pane behind this and never paints the
    // Submission List grid while we're trying to land on a submission tab.
    this.isSubmissionUrl = true;

    const submissionId = Number(rawId);
    if (!Number.isFinite(submissionId)) return;
    const submissionIdStr = String(rawId);

    // ------------------------------------------------------------------
    // Cached-tab fast path — runs on EVERY URL change (deep-link, sidebar
    // click, edit redirect, sub-state hop). Whenever the URL's :id matches
    // a submission we already have in `submissionTabs`, lock `selectedTab`
    // to that tab synchronously so the first CD pass paints with the
    // correct tab active.
    //
    // This is the single source of truth for which tab is highlighted and
    // covers two scenarios that previously needed mat-tab-group timing
    // hacks:
    //   - Edit-click on a row in the Submission List grid: the OLD
    //     component instance pushes the tab into `SubmissionService` and
    //     calls `router.navigate('/submission/:id/dashboard')`. Angular
    //     destroys the old instance and creates a fresh one. The fresh
    //     instance's `selectedTab` defaults to 0; here we flip it to the
    //     cached tab BEFORE first paint so the user lands on the new
    //     submission tab (not stranded on the Submission List).
    //   - Cross-config navigation between sibling sub-states under the
    //     same submission (e.g. `/dashboard` ↔ `/data/add-edit` matches a
    //     different route config so the component is recreated). Same
    //     fix — find the cached tab and select it synchronously.
    // ------------------------------------------------------------------
    // Match on both numeric and stringified ids so the lookup keeps working
    // when the gizmoGrid response stores SUBMISSION_ID as a string (e.g. on
    // some backend payloads / cached rows) but the URL `:id` segment parses
    // as a number — without this dual check the strict `===` would miss the
    // cached tab and we'd fall through to the slow path, which mounts the
    // Submission List grid and produces the visible "redirected to
    // submission list" regression even though the submission is in cache.
    const cachedTabIndex = this.submissionService.submissionTabs.findIndex(
      (sub) => {
        const id = (sub as any)?.SUBMISSION_ID;
        if (id == null) return false;
        return Number(id) === submissionId || String(id) === submissionIdStr;
      },
    );
    if (cachedTabIndex !== -1) {
      const desiredIndex = cachedTabIndex + 1; // +1 to skip the Submission List tab
      this.submissionService.activateSubmissionByIndex(desiredIndex);
      if (this.selectedTab !== desiredIndex) {
        this.selectedTab = desiredIndex;
      }
      this.lastAppliedSubmissionId = submissionId;
      this.deepLinkApplied = true;
      this.pendingDeepLink = null;
      SubmissionDataComponent.submissionRouteSegments[submissionIdStr] = [...this.activeUrlSegments];
      return;
    }

    // Cache miss fallback for route re-creation: keep the current submission
    // active if the singleton service already knows it, even when
    // `submissionTabs` is temporarily empty (or not yet repopulated).
    // This prevents `/submission/:id/data/add-edit` from visually dropping to
    // Submission List while waiting for a grid round-trip.
    const activeSubmission: any = this.submissionService.activeSubmission;
    const activeSubmissionId = activeSubmission?.SUBMISSION_ID;
    if (
      activeSubmission &&
      activeSubmissionId != null &&
      (Number(activeSubmissionId) === submissionId ||
        String(activeSubmissionId) === submissionIdStr)
    ) {
      const desiredIndex = this.submissionService.activateSubmission(activeSubmission as Submission);
      if (this.selectedTab !== desiredIndex) {
        this.selectedTab = desiredIndex;
      }
      this.lastAppliedSubmissionId = submissionId;
      this.deepLinkApplied = true;
      this.pendingDeepLink = null;
      SubmissionDataComponent.submissionRouteSegments[submissionIdStr] = [...this.activeUrlSegments];
      return;
    }

    // Submission isn't in the tabs cache yet — this is a true deep-link
    // entry (user pasted a URL or hit refresh on /submission/:id without
    // ever visiting the list first). Legacy ui-router resolved this parent
    // state with action_code `nvc2sx` before rendering children; do the same
    // instead of depending on the current list grid page/filter to contain
    // the row.
    this.pendingDeepLink = { submissionId, subState: normalizedSubState };
    this.deepLinkApplied = false;
    this.loadSubmissionForRoute(submissionId);
  }

  private applyPendingDeepLink(data: any): void {
    if (!this.pendingDeepLink || this.deepLinkApplied) return;

    const rows: any[] = Array.isArray(data?.data) ? data.data : [];
    if (rows.length === 0) return;

    const pendingId = this.pendingDeepLink!.submissionId;
    const pendingIdStr = String(pendingId);
    const match = rows.find((row: any) => {
      const id = row?.SUBMISSION_ID;
      if (id == null) return false;
      return Number(id) === pendingId || String(id) === pendingIdStr;
    });
    if (!match) {
      console.warn(
        '[SubmissionDataComponent] Deep-link submission_id not found in grid data:',
        this.pendingDeepLink.submissionId,
      );
      if (this.loadingSubmissionId === pendingId) {
        return;
      }
      this.navigateToListForMissingSubmission();
      return;
    }

    const submission: Submission = { ...(match as Submission), ICON: 'icon-hb-full' };
    // Deep-link path — tab switch only, URL is already correct. A push
    // here would duplicate the history entry and re-fire the route params
    // subscription. `switchToTab` activates the new tab synchronously
    // (see the cached-tab fast path in `capturePendingDeepLink`).
    const resolvedId = this.pendingDeepLink.submissionId;
    this.switchToTab(submission, { updateUrl: false });

    this.deepLinkApplied = true;
    this.pendingDeepLink = null;
    this.lastAppliedSubmissionId = resolvedId;
  }

  private loadSubmissionForRoute(submissionId: number): void {
    if (this.loadingSubmissionId === submissionId) return;
    this.loadingSubmissionId = submissionId;

    const baseUrl = generalConfigFromGlobal(this.gtwGlobal).base_url || this.baseURLs.api;
    this.subs.add(
      this.efileService.getSubmissionById(baseUrl, submissionId).subscribe(
        (submissionRow: Submission | null) => {
          this.loadingSubmissionId = null;
          if (!submissionRow) {
            this.navigateToListForMissingSubmission();
            return;
          }

          const submission: Submission = {
            ...(submissionRow as Submission),
            ICON: 'icon-hb-full',
          };
          const resolvedId = Number((submission as any)?.SUBMISSION_ID);
          this.switchToTab(submission, { updateUrl: false });
          this.deepLinkApplied = true;
          this.pendingDeepLink = null;
          this.lastAppliedSubmissionId = Number.isFinite(resolvedId) ? resolvedId : submissionId;
          this.cdr.detectChanges();
        },
        () => {
          this.loadingSubmissionId = null;
          this.navigateToListForMissingSubmission();
        },
      ),
    );
  }

  private navigateToListForMissingSubmission(): void {
    this.deepLinkApplied = true;
    this.pendingDeepLink = null;
    this.isSubmissionUrl = false;
    this.selectedTab = 0;
    this.router.navigate(
      ['/submissions-dasboard/list'],
      { queryParamsHandling: 'preserve' },
    );
    this.cdr.detectChanges();
  }

  /**
   * Normalize legacy sub-state synonyms to the canonical URL segment used
   * by `SubmissionDetailComponent`'s ngSwitch. Unknown values fall back to
   * the dashboard — mirrors the legacy default for
   * `submissions-dasboard.submission` entry without a child state.
   */
  private normalizeSubState(raw?: string): string {
    if (!raw) return 'dashboard';
    const key = raw.trim().toLowerCase();
    return SubmissionDataComponent.SUB_STATE_ALIAS_MAP[key] || 'dashboard';
  }

  /**
   * Explicit UI navigations should not silently collapse to dashboard when a
   * newly migrated state is missing from the alias map. Deep-link parsing can
   * still use `normalizeSubState()` for its safe dashboard fallback.
   */
  private normalizeRequestedSubState(raw?: string): string {
    if (!raw) return 'dashboard';
    const key = raw.trim().toLowerCase();
    return SubmissionDataComponent.SUB_STATE_ALIAS_MAP[key] || key;
  }

  /**
   * True when the given URL segment matches a sidebar-addressable state.
   * Used by `capturePendingDeepLink` to disambiguate Pattern A vs Pattern B
   * nested URLs (see comments there).
   */
  private isKnownSubState(raw?: string): boolean {
    if (!raw) return false;
    return SubmissionDataComponent.SUB_STATE_ALIAS_MAP.hasOwnProperty(
      raw.trim().toLowerCase(),
    );
  }

  /** True when the segment is a Pattern B parent (return-review, efile-scrubbing). */
  private isPatternBParent(raw?: string): boolean {
    if (!raw) return false;
    return SubmissionDataComponent.PATTERN_B_PARENT_STATES.has(
      raw.trim().toLowerCase(),
    );
  }

  /**
   * Reconstruct the route params from a raw URL string. Used by
   * CE-mounted instances (whose `route.snapshot.params` is always `{}`
   * because they're wired to the root ActivatedRoute) so the deep-link
   * resolver can still derive the submission id, parent state, and
   * child sub-state from the visible URL.
   *
   * Recognises the same shapes the routing module advertises:
   *   /submissions-dasboard/list                                    → {}
   *   /submissions-dasboard/submission/:id                          → { id }
   *   /submissions-dasboard/submission/:id/:subState                → { id, subState }
   *   /submissions-dasboard/submission/:id/:parentState/:subState   → { id, parentState, subState }
   *
   * Also accepts the correctly-spelled `submissions-dashboard` alias
   * (matched by the redirects at the bottom of `EfileRoutingModule`).
   */
  private parseSubmissionParamsFromUrl(url: string): { [k: string]: string } {
    if (!url) return {};
    // Strip query/hash + leading slash, then split into non-empty segments.
    const path = url.split('?')[0].split('#')[0].replace(/^\/+/, '');
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return {};
    const root = segments[0];
    if (root !== 'submissions-dasboard' && root !== 'submissions-dashboard') {
      return {};
    }
    if (segments[1] !== 'submission') return {};
    if (!segments[2]) return {};
    if (segments[3] && segments[4]) {
      return { id: segments[2], parentState: segments[3], subState: segments[4] };
    }
    if (segments[3]) {
      return { id: segments[2], subState: segments[3] };
    }
    return { id: segments[2] };
  }

  /**
   * Normalize route-segment payloads coming from sidebar/detail children.
   * Accepts:
   *  - string leaf:             "dashboard"
   *  - slash path:              "data/add-edit"
   *  - segment array:           ["data", "add-edit"]
   * and always returns lower-cased, trimmed segments.
   */
  private normalizeUrlSegments(
    segments?: string[] | string,
    fallbackSubState?: string,
  ): string[] {
    const pushParts = (value: string, sink: string[]) => {
      const parts = String(value)
        .split('/')
        .map((p) => p.trim().toLowerCase())
        .filter(Boolean);
      sink.push(...parts);
    };

    const normalized: string[] = [];
    if (Array.isArray(segments)) {
      segments.forEach((seg) => {
        if (seg != null) pushParts(String(seg), normalized);
      });
    } else if (typeof segments === 'string' && segments.trim().length > 0) {
      pushParts(segments, normalized);
    }

    if (normalized.length === 0 && fallbackSubState) {
      pushParts(fallbackSubState, normalized);
    }

    return normalized;
  }

  /**
   * Derive the rendered parent/child state from URL segments.
   * Handles both:
   *  - Pattern A (nested addressable): data/add-edit
   *  - Pattern B (parent + internal child): return-review/efile-diagnostics
   */
  private resolveDisplayState(
    segments: string[],
    requestedSubState?: string,
  ): { activeSubState: string; activeChildSubState: string; segments: string[] } {
    const normalizedSegments = segments.length > 0 ? segments : ['dashboard'];
    if (normalizedSegments.length >= 2) {
      const parent = normalizedSegments[0];
      const child = normalizedSegments[1];
      const childKnown = this.isKnownSubState(child);
      const parentKnown = this.isKnownSubState(parent);

      if (this.isPatternBParent(parent) && parentKnown) {
        // Pattern B (`return-review/dashboard`, `return-review/efile-diagnostics`)
        // — parent component owns internal tabs; child is NOT a sidebar screen.
        return {
          activeSubState: this.normalizeRequestedSubState(parent),
          activeChildSubState: child,
          segments: normalizedSegments,
        };
      }
      if (childKnown) {
        // Pattern A (`data/add-edit`) — child is the actual rendered sub-state.
        return {
          activeSubState: this.normalizeRequestedSubState(child),
          activeChildSubState: '',
          segments: normalizedSegments,
        };
      }
      if (parentKnown) {
        // Pattern B fallback — parent renders, child forwarded internally.
        return {
          activeSubState: this.normalizeRequestedSubState(parent),
          activeChildSubState: child,
          segments: normalizedSegments,
        };
      }
    }

    const leaf = normalizedSegments[0] || requestedSubState || 'dashboard';
    return {
      activeSubState: this.normalizeRequestedSubState(leaf),
      activeChildSubState: '',
      segments: normalizedSegments,
    };
  }

  // ---------------------------------------------------------------------------
  // Filter dropdown handlers
  // ---------------------------------------------------------------------------

  /**
   * p-dropdown emits `{originalEvent, value}` on change. `[(ngModel)]` has
   * already assigned the value by the time this fires — we keep the hook
   * around so future side-effects (telemetry, analytics, etc.) have a
   * single entry point.
   */
  taxYearSelected(event: any): void {
    const selected = Array.isArray(event?.selected)
      ? event.selected
      : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    const nextTaxYear = change?.value ?? event?.value ?? null;
    if (nextTaxYear != null) {
      this.taxYear = nextTaxYear;
    }
  }

  submissionTypeSelected(event: any): void {
    const selected = Array.isArray(event?.selected)
      ? event.selected
      : (event?.selected ? [event.selected] : []);
    const change = selected[0] ?? null;
    const nextType = change?.value ?? event?.value;
    if (nextType) {
      this.submissionType = String(nextType);
    }
  }

  // ---------------------------------------------------------------------------
  // Grid callbacks → drill-down tab (legacy `ctrlFn(callName, data, type)`)
  // ---------------------------------------------------------------------------

  /**
   * Bridge gizmoGrid callbacks back into the Angular host. Mirrors the legacy
   * `ctrl.ctrlFn(callName, data, type)` hook in `EfileElementsController.js`
   * (L216-273).
   *
   * Tab-open paths (forward to `switchToTab`):
   *   callName === 'loadSubmission' -> pencil icon on the Tax Payer column.
   *                                    Legacy broadcasts
   *                                    `submission-dashboard.addTab` and the
   *                                    dashboard controller pushes a tab for
   *                                    `_data.rowData.SUBMISSION_ID`.
   *   callName === 'viewDashboard'  -> legacy "View" link in the grid.
   *   event.type === 'link'         -> generic hyperlink cell click.
   *
   * Confirm-action paths (forward to `EfileCtrlFnDispatcherService`):
   *   callName === 'validate'          -> Return Error column pencil
   *                                       (rowData-scoped, single submission).
   *   callName === 'validateBatch'
   *            / 'generateBatch'
   *            / 'efileBatchPollAck'
   *            / 'downloadBatchAcks'   -> bulk toolbar actions.
   *
   * All other callNames (e.g. efileBatchSubmission) are owned by other
   * modals (batchSubmission.html) and are intentionally ignored.
   */
  onGridCallback(event: EfileGridCallbackEvent): void {
    if (!event) return;

    const row = this.extractRowData(event);
    const callName = String(event.callName || '').trim().toLowerCase();
    const callbackType = String(event.type || '').trim().toLowerCase();

    const isTabTrigger =
      callName === 'loadsubmission' ||
      callName === 'viewdashboard' ||
      callbackType === 'link' ||
      // Some grid configs send the edit-pencil callback as form/editSubmission.
      (callbackType === 'form' &&
        (callName === 'loadsubmission' ||
          callName === 'viewdashboard' ||
          callName === 'editsubmission'));

    if (isTabTrigger) {
      if (!row) return;
      const submission: Submission = {
        ...(row as Submission),
        ICON: 'icon-hb-full',
      };
      this.switchToTab(submission);
      return;
    }

    // Everything else that the dispatcher knows about (validate,
    // validateBatch, generateBatch, efileBatchPollAck, downloadBatchAcks,
    // enableDisableScrubbing, sub-sno-disable, sub-req-validations).
    const plan = this.ctrlFnDispatcher.plan(event);
    if (!plan) return;

    if (!plan.confirmConfig) {
      this.executePlan(plan);
      return;
    }

    this.pendingPlan = plan;
    this.confirmModalConfig = { ...this.confirmModalConfig, ...plan.confirmConfig };
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  /** Confirm handler for the `<gtw-confirmation-modal>` (yes-click). */
  onConfirmModal(): void {
    const plan = this.pendingPlan;
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.cdr.detectChanges();
    if (plan) {
      this.executePlan(plan);
    }
  }

  /** Cancel handler for the `<gtw-confirmation-modal>` (no-click / close). */
  onCancelModal(): void {
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.cdr.detectChanges();
  }

  private executePlan(plan: CtrlFnPlan): void {
    this.subs.add(
      plan.execute().subscribe((result) => {
        if (result === 'refresh') {
          this.efileGrid?.clearCache();
          this.efileGrid?.refresh(this.buildExtraParams());
        }
      }),
    );
  }

  /**
   * gizmoGrid wraps the clicked row differently depending on the callback
   * type — pencil/form callbacks pass `{ rowData: {…} }`, link cells pass
   * `{ rowData: {…} }` as well, while bulk actions pass `[{ data: {…} }, …]`.
   * Normalize all three shapes to the single-row object we need.
   */
  private extractRowData(event: EfileGridCallbackEvent): any | null {
    const data = event?.data;
    if (!data) return null;

    if (data.rowData && typeof data.rowData === 'object') {
      return data.rowData;
    }
    if (Array.isArray(data) && data.length > 0) {
      return data[0]?.data ?? data[0] ?? null;
    }
    if (Array.isArray(data?.data) && data.data.length > 0) {
      return data.data[0]?.data ?? data.data[0] ?? null;
    }
    return null;
  }

  /**
   * Activate (or create) a submission detail tab and push the matching
   * legacy URL so deep-links / back-button work end-to-end.
   *
   * Legacy reference — `EfileElementsController.js` L71-86 / L271:
   *   vm.addTab(_data) → $state.transitionTo(
   *     'submissions-dasboard.submission.dashboard', { id: tab.code });
   *
   * For NEW tab opens (Edit click on a row whose tab isn't already in the
   * tab strip) we land on `/submissions-dasboard/submission/:id/dashboard`
   * to mirror the legacy `addTab` transition. For RE-OPENS of an already-
   * open tab we instead respect the cached URL trail in
   * `submissionRouteSegments` so the user keeps their previously-viewed
   * sub-state (e.g. `/return-review/efile-sub-scrubbings`) — forcing
   * everything back to `/dashboard` here was the root cause of the
   * "URL is /return-review/<leaf> but content rendered Dashboard"
   * regression after a re-Edit-click.
   */
  switchToTab(item: Submission, opts: { updateUrl?: boolean } = {}): void {
    // gizmoGrid invokes its `callBack` from a jQuery click handler that is
    // detached from Angular's zone. Without re-entering the zone the
    // mutations below would not trigger change detection until something
    // else (router NavigationEnd, etc.) eventually ticks.
    this.zone.run(() => {
      // CRITICAL: snapshot whether the submission is already open BEFORE
      // calling `activateSubmission` (which side-effects `submissionTabs`).
      // For a re-Edit-click on a row whose tab is already in the tab strip
      // we must NOT force the URL trail back to `/dashboard` — doing that
      // throws away the user's last viewed sub-state (e.g.
      // `/return-review/efile-sub-scrubbings`) and produces the visible
      // "URL stayed on return-review but content swapped to Dashboard +
      // Dashboard sidebar item highlighted" regression. The optimistic
      // local state mutation below paints the OLD instance for one frame,
      // and if `router.navigate` resolves to the same URL (same segments)
      // there is no cross-config recreate — so the dashboard paint is the
      // FINAL render. Respect any cached trail for the existing tab.
      const targetIdRaw = (item as any)?.SUBMISSION_ID;
      const targetIdStr = targetIdRaw != null ? String(targetIdRaw) : '';
      const wasAlreadyOpen = targetIdStr.length > 0
        ? this.submissionService.submissionTabs.some((sub) => {
            const id = (sub as any)?.SUBMISSION_ID;
            return id != null && (Number(id) === Number(targetIdRaw) || String(id) === targetIdStr);
          })
        : false;
      const cachedSegments = targetIdStr.length > 0
        ? SubmissionDataComponent.submissionRouteSegments[targetIdStr]
        : null;
      const targetSegments: string[] =
        wasAlreadyOpen && Array.isArray(cachedSegments) && cachedSegments.length > 0
          ? [...cachedSegments]
          : ['dashboard'];

      const newIndex = this.submissionService.activateSubmission(item);
      item.SHOW_DROPDOWN_MENU = false;

      // Track the active submission so `capturePendingDeepLink` correctly
      // classifies subsequent sub-state-only navigations (sidebar / return-
      // review tab clicks) as "same submission" instead of fresh deep-links.
      const activatedId = Number((item as any)?.SUBMISSION_ID);
      if (Number.isFinite(activatedId)) {
        this.lastAppliedSubmissionId = activatedId;
      }

      const shouldUpdateUrl = opts.updateUrl !== false;
      if (shouldUpdateUrl) {
        // This path is an explicit grid/list edit click, not a slow deep link.
        // Keep the current component instance renderable immediately; the
        // router may reuse or recreate the component depending on the source
        // route, but the tab should not wait for that timing to settle.
        const resolved = this.resolveDisplayState(targetSegments);
        this.activeSubState = resolved.activeSubState;
        this.activeChildSubState = resolved.activeChildSubState;
        this.activeUrlSegments = [...resolved.segments];
        if (Number.isFinite(activatedId)) {
          SubmissionDataComponent.submissionRouteSegments[String(activatedId)] =
            [...resolved.segments];
        }
        this.isSubmissionUrl = true;
        this.deepLinkApplied = true;
        this.pendingDeepLink = null;
        this.loadingSubmissionId = null;
      }

      this.pendingTabActivationIndex = newIndex;

      // Activate the new tab immediately. The custom DST-style nav binds
      // `[class.active]="selectedTab === i + 1"` and `*ngIf="selectedTab
      // === ..."` directly, so this synchronous assignment is enough —
      // there is no MatTabGroup `[selectedIndex]` clamp to fight against.
      this.setActiveTab(newIndex);
      this.cdr.detectChanges();

      setTimeout(() => {
        if (this.pendingTabActivationIndex !== newIndex) return;
        this.setActiveTab(newIndex);
        this.cdr.detectChanges();
      }, 0);

      if (!shouldUpdateUrl) {
        setTimeout(() => {
          if (this.pendingTabActivationIndex === newIndex) {
            this.pendingTabActivationIndex = null;
          }
        }, 0);
        return;
      }

      const id = (item as any)?.SUBMISSION_ID;
      if (id == null) return;
      SubmissionDataComponent.submissionRouteSegments[String(id)] = [...targetSegments];

      // Preserve any query params (notably `client_key`) so deep-links like
      // `/submission/:id/dashboard?client_key=92` keep the workspace context
      // when the route changes after a tab switch. After Angular destroys
      // and recreates this component on the cross-config navigation, the
      // fresh instance's `capturePendingDeepLink` will re-flip
      // `selectedTab` to the cached tab — see the cached-tab fast path.
      this.router.navigate(
        ['/submissions-dasboard/submission', id, ...targetSegments],
        { queryParamsHandling: 'preserve' },
      ).then(() => {
        // If Router reuses the current component instance (or skips a
        // duplicate navigation), keep the explicitly opened tab active.
        this.setActiveTab(newIndex);
        this.cdr.detectChanges();

        // Keep the guard alive through the next tab-strip tick. On the first
        // edit click the tab is created and the route changes in the same
        // turn; a late index-0 emission can arrive after navigation resolves.
        setTimeout(() => {
          if (this.pendingTabActivationIndex === newIndex) {
            this.pendingTabActivationIndex = null;
          }
        }, 0);
      });
    });
  }

  /**
   * Close icon (`x`) on a submission tab. Removes the submission from the
   * `submissionTabs` list, snaps `selectedTab` to a valid neighbour, and
   * navigates back to the Submission List when the tab being closed is
   * the active one (legacy: closing the active tab in `uib-tabset` falls
   * back to the first tab).
   *
   * `event.stopPropagation()` prevents the click from bubbling into the
   * mat-tab header — without it, MatTabGroup would activate the tab being
   * closed (push its URL, mount its detail content) BEFORE we splice it
   * out, which is what the user sees as "tab click not working".
   */
  closeSubmissionTab(event: MouseEvent, index: number): void {
    event.stopPropagation();
    event.preventDefault();

    const tabs = this.submissionService.submissionTabs;
    if (index < 0 || index >= tabs.length) return;

    const closedSubmissionId = Number((tabs[index] as any)?.SUBMISSION_ID);
    const wasActive = this.selectedTab === index + 1;

    tabs.splice(index, 1);

    // Tabs are 1-indexed in `selectedTab` because index 0 is the
    // Submission List. Recompute a safe target so the MatTabGroup never
    // ends up pointing past the end of the *ngFor.
    if (tabs.length === 0) {
      this.selectedTab = 0;
    } else if (wasActive) {
      // Prefer the tab that took the closed slot; if we just removed the
      // last one, step back one position so we don't land on an empty
      // index.
      const nextTabIndex = Math.min(index, tabs.length - 1);
      this.selectedTab = nextTabIndex + 1;
      this.submissionService.activateSubmissionByIndex(this.selectedTab);
    } else if (this.selectedTab > index + 1) {
      // We removed a tab to the LEFT of the current one — shift selection
      // left by one to keep the same submission focused.
      this.selectedTab = this.selectedTab - 1;
    }

    if (this.lastAppliedSubmissionId === closedSubmissionId) {
      this.lastAppliedSubmissionId = null;
    }
    if (Number.isFinite(closedSubmissionId)) {
      delete SubmissionDataComponent.submissionRouteSegments[String(closedSubmissionId)];
    }

    if (this.selectedTab === 0) {
      this.activeSubState = '';
      this.activeChildSubState = '';
      this.activeUrlSegments = [];
      this.submissionService.activateSubmissionByIndex(0);
      this.router.navigate(
        ['/submissions-dasboard/list'],
        { queryParamsHandling: 'preserve' },
      );
    } else {
      // Snapped to a neighbour submission tab. Sync display state to the
      // neighbour's stored nested route BEFORE pushing the URL so the
      // freshly-mounted pane (the *ngIf flips when selectedTab changes)
      // doesn't render with the closed tab's stale subState. See the
      // matching block in `selectedTabChange` for the full rationale.
      const active = this.submissionService.activeSubmission;
      const id = (active as any)?.SUBMISSION_ID;
      if (id != null) {
        const segments = this.routeSegmentsForSubmissionId(id);
        const resolved = this.resolveDisplayState(segments);
        this.activeSubState = resolved.activeSubState;
        this.activeChildSubState = resolved.activeChildSubState;
        this.activeUrlSegments = resolved.segments;
        this.router.navigate(
          ['/submissions-dasboard/submission', id, ...resolved.segments],
          { queryParamsHandling: 'preserve' },
        );
      }
    }

    this.cdr.detectChanges();
  }

  /**
   * Direct user click on the custom DST-style tab strip (Submission List
   * anchor or one of the open submission tabs). Pushes the matching
   * legacy URL — programmatic activations (deep-link fast path,
   * `switchToTab`) bypass this method entirely and assign `selectedTab`
   * directly, so we no longer need a "suppress" flag here.
   *
   * Submission tabs (any index other than the list tab) always navigate to
   * `/submission/:id/dashboard` so the dashboard is the landing screen;
   * nested routes are still opened from the grid (`switchToTab`) or the
   * sidebar (`handleSubStateChange`). After `Router.navigate` resolves,
   * dashboard state is re-applied so `capturePendingDeepLink` cannot leave
   * nested `activeSubState` in place when re-clicking an already-open tab.
   */
  selectedTabChange($event: number): void {
    // During deep-link bootstrap (e.g. `/submission/:id/data/add-edit`),
    // an initial index `0` can leak through before the submission tab is
    // restored. Treating that as a real user click sends the app to
    // `/submissions-dasboard/list`, which looks like Add/Edit "failed to load".
    // Ignore index-0 emissions until the pending deep-link is resolved.
    if ($event === 0 && this.pendingDeepLink && !this.deepLinkApplied) {
      return;
    }

    // Programmatic edit/grid callbacks call switchToTab() and can be followed
    // by a stale index-0 emission from the tab shell while the router settles.
    // Do not let that transient event undo the explicit activation.
    if ($event === 0 && this.pendingTabActivationIndex !== null) {
      this.setActiveTab(this.pendingTabActivationIndex);
      this.cdr.detectChanges();
      return;
    }

    this.pendingTabActivationIndex = null;

    if ($event === 0) {
      this.setActiveTab($event);
      this.activeSubState = '';
      this.activeChildSubState = '';
      this.activeUrlSegments = [];
      // "Submission List" tab re-selected — mirror legacy
      // submissions-dasboard.list URL. Preserve query params so the
      // workspace context (e.g. `client_key`) survives the round-trip.
      this.router.navigate(
        ['/submissions-dasboard/list'],
        { queryParamsHandling: 'preserve' },
      );
      return;
    }

    // Before flipping `selectedTab` (which causes the *ngFor + *ngIf in
    // submission-data.html to UNMOUNT the previous tab's submission-pane
    // and MOUNT the target tab's pane fresh), align `activeSubState` /
    // `activeChildSubState` / `activeUrlSegments` with what we will paint.
    //
    // Tab-strip clicks always open the submission **dashboard** — even if
    // the user previously left that tab on Return Review, Add/Edit, etc.
    // (Grid drill-down via `switchToTab` still restores cached nested routes
    // for an already-open tab so re-Edit does not flatten the URL.)
    //
    // Also pre-sync so the new pane never briefly inherits the *previous*
    // tab's `subState` (wrong ngSwitch + wrong HTTP) before the router
    // settles.
    const targetSubmission = this.selectedSubmissions[$event - 1];
    const fallbackActive = this.submissionService.activeSubmission;
    const targetId =
      (targetSubmission as any)?.SUBMISSION_ID ??
      (fallbackActive as any)?.SUBMISSION_ID;
    if (targetId == null) {
      this.setActiveTab($event);
      return;
    }

    const segments = ['dashboard'];
    SubmissionDataComponent.submissionRouteSegments[String(targetId)] = [...segments];
    const resolved = this.resolveDisplayState(segments);
    this.activeSubState = resolved.activeSubState;
    this.activeChildSubState = resolved.activeChildSubState;
    this.activeUrlSegments = resolved.segments;
    this.setActiveTab($event);
    this.cdr.detectChanges();

    const idStr = String(targetId);
    const reassertDashboardShell = () => {
      const dash = ['dashboard'];
      SubmissionDataComponent.submissionRouteSegments[idStr] = [...dash];
      const r = this.resolveDisplayState(dash);
      this.activeSubState = r.activeSubState;
      this.activeChildSubState = r.activeChildSubState;
      this.activeUrlSegments = r.segments;
      this.cdr.detectChanges();
    };

    this.router
      .navigate(
        ['/submissions-dasboard/submission', targetId, ...resolved.segments],
        { queryParamsHandling: 'preserve' },
      )
      .then(() => {
        // `route.params` / `capturePendingDeepLink` can still run with a stale
        // snapshot while the same submission tab remains selected (user clicks
        // an already-open tab that was left on Return Review, Add/Edit, etc.).
        // That briefly restores nested `activeSubState` from the old URL.
        // Re-apply the tab-strip contract — always the dashboard shell — after
        // navigation settles (mirrors always landing on dashboard for tab UI).
        reassertDashboardShell();
      });
  }

  /**
   * Sidebar menu clicks inside `<submission-detail>` bubble up through
   * `(subStateChange)`. We push the legacy URL for the active submission
   * so the browser history + deep-linking still work, and the template's
   * `[subState]` binding swaps the inline content.
   *
   * Accepts either:
   *   - a plain sub-state key (legacy callers / programmatic API), OR
   *   - `{subState, urlSegments}` from the sidebar (preferred), so we can
   *     reconstruct the nested legacy URL `/data/add-edit` for the
   *     `submissions-dasboard.submission.data.addEdit` child state
   *     (efile.js L149) instead of flattening it to `/add-edit`.
   */
  handleSubStateChange(payload: string | { subState: string; urlSegments: string[] }): void {
    const subState = typeof payload === 'string' ? payload : payload.subState;
    const rawSegments = typeof payload === 'string'
      ? payload
      : (payload.urlSegments && payload.urlSegments.length > 0
          ? payload.urlSegments
          : payload.subState);
    const urlSegments = this.normalizeUrlSegments(rawSegments, subState);
    const resolved = this.resolveDisplayState(urlSegments, subState);

    // A sidebar click is an explicit user navigation inside the already
    // active submission pane. Do not allow a still-pending tab-open guard
    // (whose default route is dashboard) to reassert dashboard over this
    // requested child screen on the first click.
    this.pendingTabActivationIndex = null;

    const active =
      this.selectedTab > 0
        ? (this.selectedSubmissions[this.selectedTab - 1] || this.submissionService.activeSubmission)
        : this.submissionService.activeSubmission;
    const id = (active as any)?.SUBMISSION_ID ?? this.route.snapshot.params?.['id'];
    if (id == null) return;
    if (this.selectedTab > 0) {
      this.submissionService.activateSubmissionByIndex(this.selectedTab);
    }
    this.deepLinkApplied = true;
    this.pendingDeepLink = null;
    this.cdr.detectChanges();

    // Stash the segment trail and submission service activation BEFORE
    // navigating so the cached-tab fast path inside the new instance's
    // `capturePendingDeepLink` (cross-config navigation case) can land on
    // the right tab + URL synchronously on first paint. THIS MUST HAPPEN
    // for both same-config and cross-config paths because the new
    // instance's constructor + ngOnInit reads from these caches before
    // its first CD pass.
    SubmissionDataComponent.submissionRouteSegments[String(id)] = [...resolved.segments];
    if (this.selectedTab > 0) {
      this.submissionService.activateSubmissionByIndex(this.selectedTab);
    }

    // Detect whether this navigation crosses route configs. The route
    // table has three siblings the user can hop between via the sidebar:
    //   1 segment after `/submission/:id`  → flat sub-state (`/dashboard`)
    //   2 segments → nested (`/data/add-edit`)
    // A 1↔2 hop matches a different `Routes` entry (`:subState` vs
    // `:parentState/:subState`), so Angular DESTROYS the current
    // SubmissionDataComponent and creates a fresh one. If we mutate
    // local state + run `cdr.detectChanges()` here BEFORE the navigation,
    // the dying instance briefly mounts the destination's child
    // (e.g. `<gtw-efile-elements-data>` for Add/Edit), runs its
    // ngOnInit / first HTTP, and then is immediately torn down. That
    // teardown can race against the new instance's first paint —
    // observed symptom: URL lands on `/data/add-edit` but the new
    // instance renders the dashboard with the "Dashboard" sidebar item
    // highlighted on the FIRST click; a SECOND click is same-config so
    // there is no recreate and the local mutation here paints correctly.
    //
    // For cross-config navigations skip the optimistic mutation. The
    // new instance's `capturePendingDeepLink` (called synchronously
    // from `ngOnInit`) reads the URL params before first CD and sets
    // `activeSubState`/`activeChildSubState` correctly on the FIRST
    // paint of the new component.
    const currentSegmentCount = this.activeUrlSegments.length;
    const targetSegmentCount = resolved.segments.length;
    const isSameConfigNav = currentSegmentCount === targetSegmentCount;

    if (isSameConfigNav) {
      // Same component instance will be reused — pre-paint so the
      // visible pane changes immediately without waiting on the
      // route.params subscription.
      this.activeSubState = resolved.activeSubState;
      this.activeChildSubState = resolved.activeChildSubState;
      this.activeUrlSegments = resolved.segments;
      this.isSubmissionUrl = true;
      this.deepLinkApplied = true;
      this.pendingDeepLink = null;
      this.cdr.detectChanges();
    }

    // Preserve query params (notably `client_key`) when the sidebar
    // navigates between sub-states inside the same submission so the
    // workspace context isn't dropped on every menu click.
    this.router.navigate(
      ['/submissions-dasboard/submission', id, ...resolved.segments],
      { queryParamsHandling: 'preserve' },
    );
  }

  // ---------------------------------------------------------------------------
  // Appkit dropdown helpers
  // ---------------------------------------------------------------------------

  setAriaLabel(item: any, contextLabel: string = 'Option'): string {
    const label = item?.label || item?.text || '-';
    return `${contextLabel} option ${label}`.trim();
  }

  onDropdownSelectItem(_event: any): void {
    // Appkit forwards per-item selection events; we rely on the
    // parent `onSelect` (taxYearSelected / submissionTypeSelected) instead.
  }

  /**
   * Single activation path used by:
   *  - manual tab clicks (`selectedTabChange`)
   *  - new tab opens from grid actions (`switchToTab`)
   * Keeps tab-highlight and active-submission state in sync.
   */
  private setActiveTab(tabIndex: number): void {
    this.selectedTab = tabIndex;
    this.submissionService.activateSubmissionByIndex(tabIndex);
  }

  private routeSegmentsForSubmissionId(submissionId: any): string[] {
    const key = String(submissionId ?? '');
    const mapped = SubmissionDataComponent.submissionRouteSegments[key];
    if (Array.isArray(mapped) && mapped.length > 0) {
      return mapped;
    }
    // No memory for this tab yet — never inherit `this.activeUrlSegments`
    // (which belongs to a DIFFERENT tab and would send the user to the
    // wrong screen on switch). Default to the legacy landing child state.
    return ['dashboard'];
  }
}

